import { ReactNode } from "react";
import {
    Box,
    ListSubheader,
    MenuItem,
    Select,
    SelectChangeEvent,
    styled,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Folder } from "lucide-react";

export interface DropdownOption {
    value: string;
    label: string;
    icon?: ReactNode;
    type?: "header" | "parent" | "child";
}

interface DropdownProps {
    value: string;
    onChange: (value: string) => void;
    options: DropdownOption[];
    placeholder?: string;
    minWidth?: number | string;
}

const StyledSelect = styled(Select<string>)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "6px",
    height: "32px",
    "& .MuiSelect-select": {
        padding: "0.25rem 0.75rem",
        paddingRight: "2rem",
        fontSize: "0.8rem",
        color: theme.palette.text.primary,
        fontWeight: 500,
        display: "flex",
        alignItems: "center",
    },
    "&:hover": {
        borderColor: theme.palette.grey[400],
    },
    "&.Mui-focused": {
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 3px ${theme.palette.primary.main}14`,
    },
    "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
    },
    "& .MuiSelect-icon": {
        right: "0.5rem",
        color: theme.palette.grey[500],
        fontSize: "1rem",
        transition: "transform 0.2s ease",
    },
}));

const Dropdown = ({
    value,
    onChange,
    options,
    placeholder,
    minWidth = 200,
}: DropdownProps) => {
    const handleChange = (event: SelectChangeEvent<string>) => {
        onChange(event.target.value);
    };

    return (
        <Box sx={{ minWidth }}>
            <StyledSelect
                value={value}
                onChange={handleChange}
                displayEmpty
                fullWidth
                IconComponent={KeyboardArrowDownIcon}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            borderRadius: "8px",
                            marginTop: "4px",
                            border: (theme) =>
                                `1px solid ${theme.palette.divider}`,
                            boxShadow:
                                "0 4px 12px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04)",
                            "& .MuiMenuItem-root": {
                                fontSize: "0.8rem",
                                color: (theme) => theme.palette.text.primary,
                                padding: "6px 12px",
                                borderRadius: "4px",
                                mx: "4px",
                                transition: "background-color 0.1s ease",
                                "&:hover": {
                                    backgroundColor: (theme) =>
                                        theme.palette.grey[50],
                                },
                                "&.Mui-selected": {
                                    backgroundColor: (theme) =>
                                        `${theme.palette.primary.main}0A`,
                                    color: (theme) =>
                                        theme.palette.primary.main,
                                    fontWeight: 600,
                                    "&:hover": {
                                        backgroundColor: (theme) =>
                                            `${theme.palette.primary.main}14`,
                                    },
                                },
                            },
                        },
                    },
                }}
            >
                {placeholder && value === "" && (
                    <MenuItem value="" disabled>
                        {placeholder}
                    </MenuItem>
                )}
                {options.map((option) => {
                    if (option.type === "header") {
                        return (
                            <ListSubheader
                                key={option.value}
                                sx={{
                                    fontSize: "0.65rem",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.06em",
                                    color: "text.disabled",
                                    lineHeight: 1,
                                    pt: 1.5,
                                    pb: 0.75,
                                    px: 2,
                                    backgroundColor: "background.paper",
                                }}
                            >
                                {option.label}
                            </ListSubheader>
                        );
                    }
                    if (option.type === "parent") {
                        return (
                            <MenuItem
                                key={option.value}
                                value={option.value}
                                sx={{ fontWeight: 600 }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        display: "inline-flex",
                                        mr: 1,
                                        verticalAlign: "middle",
                                        color: "text.secondary",
                                    }}
                                >
                                    <Folder size={14} />
                                </Box>
                                {option.label}
                            </MenuItem>
                        );
                    }
                    if (option.type === "child") {
                        return (
                            <MenuItem
                                key={option.value}
                                value={option.value}
                                sx={{
                                    pl: "2rem !important",
                                    color: "text.secondary",
                                    fontSize: "0.78rem !important",
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        display: "inline-flex",
                                        mr: 0.75,
                                        verticalAlign: "middle",
                                        color: "text.disabled",
                                        opacity: 0.7,
                                    }}
                                >
                                    <Folder size={12} />
                                </Box>
                                {option.label}
                            </MenuItem>
                        );
                    }
                    return (
                        <MenuItem key={option.value} value={option.value}>
                            {option.icon && (
                                <Box
                                    component="span"
                                    sx={{
                                        display: "inline-flex",
                                        mr: 1,
                                        verticalAlign: "middle",
                                    }}
                                >
                                    {option.icon}
                                </Box>
                            )}
                            {option.label}
                        </MenuItem>
                    );
                })}
            </StyledSelect>
        </Box>
    );
};

export default Dropdown;
